import React from 'react';
import { cn } from '../../lib/utils';
import { AlertCircle, LogIn, LogOut, Settings2 } from 'lucide-react';
import { CoreHUD } from './CoreHUD';
import { appProfiler } from '../../utils/appProfiler';
import { getUserEmail } from '../../lib/admin';


export interface HeaderTerminalProps {
  uiHiddenLevel: number;
  showUiControls: boolean;
  useRealData: boolean;
  isEditingSymbol: boolean;
  setIsEditingSymbol: (val: boolean) => void;
  symbol: string;
  setSymbol: (val: string) => void;
  timeframe: string;
  showMobileData: boolean;
  setShowMobileData: (val: boolean) => void;
  lastPrice: number | null;
  priceChange: number;
  currentMomentum?: number | null;
  candleCloseTime?: number;
  latencyMs: number | null;
  isFirestoreOffline: boolean;
  user: any;
  setShowLogoutConfirm: (val: boolean) => void;
  loginWithGoogle: () => void;
  showLogoutConfirm: boolean;
  handleLogout: () => void;
  setShowMobileSettings: (val: boolean) => void;
  setRadialMenuPos: (pos: any) => void;
}

export const HeaderTerminal = React.memo<HeaderTerminalProps>(({
  uiHiddenLevel,
  showUiControls,
  useRealData,
  isEditingSymbol,
  setIsEditingSymbol,
  symbol,
  setSymbol,
  timeframe,
  showMobileData,
  setShowMobileData,
  lastPrice,
  priceChange,
  currentMomentum,
  candleCloseTime,
  latencyMs,
  isFirestoreOffline,
  user,
  setShowLogoutConfirm,
  loginWithGoogle,
  showLogoutConfirm,
  handleLogout,
  setShowMobileSettings,
  setRadialMenuPos,
}) => {
  return (
    <div className={cn(
      "absolute top-4 md:top-6 lg:top-8 left-4 md:left-6 lg:left-8 right-4 md:right-6 lg:right-8 z-20 flex justify-between items-start pointer-events-none transition-all duration-1000",
      uiHiddenLevel === 2 ? "opacity-0 invisible" : "opacity-100 visible"
    )}>
      {/* Left Side / Header */}
      <div className="pointer-events-auto flex flex-col gap-2 w-auto max-w-[75vw]">
        <CoreHUD 
          isEditingSymbol={isEditingSymbol}
          setIsEditingSymbol={setIsEditingSymbol}
          symbol={symbol}
          setSymbol={setSymbol}
          timeframe={timeframe}
          lastPrice={lastPrice}
          priceChange={priceChange}
          currentMomentum={currentMomentum}
          candleCloseTime={candleCloseTime}
          latencyMs={latencyMs}
          setRadialMenuPos={setRadialMenuPos}
          extraControls={
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMobileData(!showMobileData); }}
              className={cn(
                "p-1.5 md:p-2 rounded-lg border transition-all duration-200 flex items-center justify-center bg-[#0a0e27]/80 backdrop-blur-md shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-pointer", 
                showMobileData ? "bg-[#00d9ff]/20 border-[#00d9ff] text-[#00d9ff]" : "border-[#00d9ff]/30 text-[#666666] hover:text-[#00d9ff] hover:border-[#00d9ff]"
              )}
              title="Market Data"
            >
              <AlertCircle size={16} className="md:w-5 md:h-5" />
            </button>
          }
        />

        {/* Row 4: Subtitles & Offline indicators */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1">
          <span className="text-[10px] md:text-xs uppercase tracking-[2px] text-[#666666] hidden sm:inline-block">
            {useRealData ? (
              <span key="market-text">Real-Time Market Visualization</span>
            ) : (
              <span key="price-text">Real 3D Price Visualization</span>
            )}
          </span>
          {isFirestoreOffline && (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-[#ff3d67]/20 text-[#ff3d67] border border-[#ff3d67]/30 uppercase tracking-tighter animate-pulse">
              Offline
            </span>
          )}
        </div>
        
        <div className={cn("mt-2 flex items-center gap-2 transition-all duration-1000", uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible pointer-events-none" : "opacity-100 visible")}>
          {user ? (
            <button 
              key="logged-user-button"
              onClick={() => setShowLogoutConfirm(true)}
              className="flex items-center justify-center bg-[#0a0e27]/80 backdrop-blur border border-[#00d9ff]/30 p-1.5 rounded-full hover:bg-[#00d9ff]/10 hover:border-[#00d9ff]/50 transition-all shadow-[0_0_10px_rgba(0,0,0,0.5)] cursor-pointer"
              title={`Logado como ${user.displayName || getUserEmail(user)}. Clique para sair.`}
            >
              {user.photoURL ? (
                <img src={user.photoURL} alt="Profile" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#00d9ff]/20 flex items-center justify-center text-[#00d9ff] font-bold text-xs">
                  {(user.displayName || getUserEmail(user) || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </button>
          ) : (
            <button 
              key="anonymous-login-button"
              onClick={loginWithGoogle}
              className="flex items-center gap-2 bg-[#00d9ff]/10 hover:bg-[#00d9ff]/20 text-[#00d9ff] border border-[#00d9ff]/30 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer"
            >
              <LogIn size={14} />
              <span>Fazer Login</span>
            </button>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200 pointer-events-auto">
          <div className="bg-[#0a0e27]/90 border border-[#ff3d67]/30 p-6 rounded-2xl w-full max-w-[320px] shadow-[0_0_50px_rgba(255,61,103,0.2)]">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-[#ff3d67]/10 flex items-center justify-center text-[#ff3d67] mb-2">
                <LogOut size={24} />
              </div>
              <h3 className="text-[#00d9ff] text-[14px] font-bold uppercase tracking-[2px]">Confirmar Saída</h3>
              <p className="text-[#666666] text-[11px] leading-relaxed">
                Tem certeza que deseja sair da sua conta? Suas configurações salvas permanecerão seguras.
              </p>
              <div className="flex w-full gap-3 pt-2">
                <button 
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 py-2.5 rounded-lg border border-[#00d9ff]/20 text-[#666666] text-[10px] font-bold uppercase tracking-wider hover:bg-white/5 transition-all cursor-pointer"
                >
                  Não
                </button>
                <button 
                  onClick={handleLogout}
                  className="flex-1 py-2.5 rounded-lg bg-[#ff3d67] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#ff3d67]/80 transition-all shadow-[0_0_15px_rgba(255,61,103,0.3)] cursor-pointer"
                >
                  Sim, Sair
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Side / Mobile Buttons */}
      <div className={cn("flex flex-col items-end gap-2 md:gap-3 pointer-events-auto shrink-0 mt-1 md:mt-0 transition-all duration-1000", uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible" : "opacity-100 visible")}>
        <div className="flex flex-col items-end gap-2 pr-1">
          <button 
            onClick={() => setShowMobileSettings(true)}
            className="p-1.5 md:p-3 rounded-full border border-[#00d9ff]/30 text-[#666666] hover:text-[#00d9ff] hover:bg-[#00d9ff]/10 hover:border-[#00d9ff] transition-all duration-200 flex items-center justify-center bg-[#0a0e27]/80 backdrop-blur-md shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-pointer"
            title="Configurações e Timeframe"
          >
            <Settings2 size={24} className="md:w-6 md:h-6" />
          </button>
        </div>
      </div>
    </div>
  );
});

export default HeaderTerminal;
